import React, { Suspense, lazy, memo } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const LazyArchivesModal = lazy(() => import("../ArchivesModal"));
const LazyTokenUsageLogs = lazy(() => import("../TokenUsageLogs"));
const LazyGameCenter = lazy(() => import("./GameCenter"));
const LazySelfAssessmentDialog = lazy(() => import("./SelfAssessmentDialog"));
const LazyCapabilityProfileDialog = lazy(() => import("./CapabilityProfileDialog"));
const LazyRecruitEncouragementPanel = lazy(() => import("./RecruitEncouragementPanel"));

const ModalLayer = ({
  isArchivesModalOpen,
  setIsArchivesModalOpen,
  archivesList,
  handleDeleteArchive,
  handleRecoverSpecificArchive,
  handleEditProjectName,
  handleRequestArchives,
  currentUser,
  isSelfAssessmentDialogOpen,
  setIsSelfAssessmentDialogOpen,
  selfAssessmentStatus,
  handleSelfAssessmentSubmitSuccess,
  isCapabilityProfileDialogOpen,
  setIsCapabilityProfileDialogOpen,
  currentStudentId,
  isRecruitEncouragementPanelOpen,
  setIsRecruitEncouragementPanelOpen,
  isTokenUsageLogsOpen,
  setIsTokenUsageLogsOpen,
  tokenGrant,
  tokenBalance,
  totalTokens,
  planName,
  showGameCenter,
  setShowGameCenter,
  gameProfile,
  gameConfig,
  fetchAchievementWall,
  fetchGameLeaderboard,
  fetchSkillHistory,
  fetchGrowthMap,
  achievementWallData,
  leaderboardData,
  skillHistoryData,
  growthMapData,
  todayYouLiveCards,
  onMarkTodayYouRead,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {isArchivesModalOpen && (
        <Suspense fallback={null}>
          <LazyArchivesModal
            isOpen={isArchivesModalOpen}
            onClose={() => setIsArchivesModalOpen(false)}
            archives={archivesList}
            onDelete={handleDeleteArchive}
            onRecover={handleRecoverSpecificArchive}
            onEditName={handleEditProjectName}
            onRequestArchives={handleRequestArchives}
            isSchoolUser={currentUser?.isSchoolUser}
            accentColor={currentUser?.branding?.themeColor}
          />
        </Suspense>
      )}

      <Suspense fallback={null}>
        <LazySelfAssessmentDialog
          open={isSelfAssessmentDialogOpen}
          onClose={() => setIsSelfAssessmentDialogOpen(false)}
          currentUser={currentUser}
          selfAssessmentStatus={selfAssessmentStatus}
          onSubmitSuccess={handleSelfAssessmentSubmitSuccess}
        />
      </Suspense>

      <Suspense fallback={null}>
        <LazyCapabilityProfileDialog
          open={isCapabilityProfileDialogOpen}
          onClose={() => setIsCapabilityProfileDialogOpen(false)}
          studentId={currentStudentId}
        />
      </Suspense>

      <Suspense fallback={null}>
        <LazyRecruitEncouragementPanel
          open={isRecruitEncouragementPanelOpen}
          onClose={() => setIsRecruitEncouragementPanelOpen(false)}
          studentName={currentUser?.name}
        />
      </Suspense>

      <Dialog
        open={isTokenUsageLogsOpen}
        onClose={() => setIsTokenUsageLogsOpen(false)}
        maxWidth="lg"
        fullWidth
        className="workbench-dialog token-usage-logs-dialog"
        PaperProps={{
          className: "workbench-dialog-paper",
          style: {
            height: "85vh",
            maxHeight: "900px",
            width: "min(95vw, 1200px)",
            "--workbench-accent": currentUser?.branding?.themeColor || "#4D8BFF",
          },
        }}
      >
        <DialogTitle className="workbench-dialog-title">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{t('modal_layer_my_usage')}</span>
            <IconButton className="workbench-dialog-close" onClick={() => setIsTokenUsageLogsOpen(false)} aria-label={t('digital_human_close')}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="workbench-dialog-content" style={{ padding: 0, overflow: "hidden" }}>
          <Suspense fallback={null}>
            <LazyTokenUsageLogs
              userInfo={{
                ...currentUser,
                user_type: currentUser?.isSchoolUser
                  ? "campus"
                  : currentUser?.isCasUser
                  ? (currentUser?.user_type || "bupt_cas")
                  : "guest",
              }}
              tokenGrant={tokenGrant}
              tokenBalance={tokenBalance}
              totalTokens={totalTokens}
              planName={planName}
            />
          </Suspense>
        </DialogContent>
      </Dialog>

      <Suspense fallback={null}>
        <LazyGameCenter
          open={showGameCenter}
          onClose={() => setShowGameCenter(false)}
          gameProfile={gameProfile}
          gameConfig={gameConfig}
          currentUser={currentUser}
          onLoadAchievementWall={() => {
            const studentId =
              currentUser?.unified_user_id ||
              currentUser?.studentID ||
              currentUser?.student_id;
            if (studentId) {
              fetchAchievementWall(studentId);
            } else {
              console.warn("🎮 [App] 无法获取学生ID，无法加载成就墙数据");
            }
          }}
          onLoadLeaderboard={() => {
            const studentId =
              currentUser?.unified_user_id ||
              currentUser?.studentID ||
              currentUser?.student_id;
            if (studentId) {
              fetchGameLeaderboard(studentId);
            } else {
              console.warn("🎮 [App] 无法获取学生ID，无法加载排行榜数据");
            }
          }}
          onLoadSkillHistory={(days = 30) => {
            const studentId =
              currentUser?.unified_user_id ||
              currentUser?.studentID ||
              currentUser?.student_id;
            if (studentId) {
              const allSkills = [
                "工程知识",
                "问题分析",
                "设计/开发解决方案",
                "研究",
                "使用现代工具",
                "工程与可持续发展",
                "工程伦理和职业规范",
                "个人与团队",
                "沟通",
                "项目管理",
                "终身学习",
              ];
              fetchSkillHistory(studentId, allSkills, days);
            } else {
              console.warn("🎮 [App] 无法获取学生ID，无法加载能力历史数据");
            }
          }}
          onLoadGrowthMap={() => {
            const studentId =
              currentUser?.unified_user_id ||
              currentUser?.studentID ||
              currentUser?.student_id;
            if (studentId) {
              fetchGrowthMap(studentId);
            } else {
              console.warn("🎮 [App] 无法获取学生ID，无法加载成长地图数据");
            }
          }}
          achievementWallData={achievementWallData}
          leaderboardData={leaderboardData}
          skillHistoryData={skillHistoryData}
          growthMapData={growthMapData}
          todayYouLiveCards={todayYouLiveCards}
          onMarkTodayYouRead={onMarkTodayYouRead}
        />
      </Suspense>
    </>
  );
};

export default memo(ModalLayer);
